import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  alpha,
  useTheme,
  Icon,
  Tooltip,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useAuth } from "../../../auth/hooks/useAuth";
import { grnService } from "../../services/grn.service";
import { poService } from "../../services/po.service";
import {
  GoodsReceiptNote,
  GRNItem,
  ItemCondition,
  CreateGRNRequest,
  UpdateGRNRequest,
  GRNAutoFillData,
} from "../../types/grn.types";
import { IngredientConfigDialog } from "./IngredientConfigDialog";
import { PurchaseOrder } from "../../types/po.types";

interface GRNFormProps {
  grn?: GoodsReceiptNote;
  po_id?: string; // For creating GRN from PO
  onSave: (grn: GoodsReceiptNote) => void;
  onCancel: () => void;
}

interface POOption {
  _id: string;
  po_number: string;
  vendor_name: string;
  total_amount: number;
  currency: string;
  received_at: number;
}

interface WarehouseOption {
  id: string;
  name: string;
  code: string;
  location?: string;
  is_active: boolean;
}

export const GRNForm: React.FC<GRNFormProps> = ({
  grn,
  po_id,
  onSave,
  onCancel,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [selectedPO, setSelectedPO] = useState<POOption | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] =
    useState<WarehouseOption | null>(null);
  const [storageLocation, setStorageLocation] = useState("");
  const [deliveryReceiptNumber, setDeliveryReceiptNumber] = useState("");
  const [receiverNotes, setReceiverNotes] = useState("");
  const [items, setItems] = useState<GRNItem[]>([]);

  // Options
  const [poOptions, setPOOptions] = useState<POOption[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>(
    []
  );

  // Loading states
  const [poLoading, setPOLoading] = useState(false);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [autoFillLoading, setAutoFillLoading] = useState(false);

  // Debug: Component render state
  // console.log("🏭 Component render - warehouseOptions:", warehouseOptions, "warehouseLoading:", warehouseLoading);

  // Error state
  const [errors, setErrors] = useState<string[]>([]);

  // Ingredient Configuration Dialog State (Phase 2)
  const [ingredientConfigOpen, setIngredientConfigOpen] = useState(false);
  const [selectedItemForConfig, setSelectedItemForConfig] =
    useState<GRNItem | null>(null);

  // Condition options
  const conditionOptions: Array<{
    value: ItemCondition;
    label: string;
    color: string;
  }> = [
    { value: "good", label: "Good", color: "success" },
    { value: "damaged", label: "Damaged", color: "error" },
    {
      value: "partially_damaged",
      label: "Partially Damaged",
      color: "warning",
    },
  ];

  useEffect(() => {
    console.log("GRNForm useEffect triggered - loading warehouses and POs");
    loadWarehouses();
    if (!grn && !po_id) {
      loadReceivedPOs();
    }
  }, []);

  useEffect(() => {
    if (grn) {
      populateFormFromGRN();
    } else if (po_id) {
      loadPOData(po_id);
    }
  }, [grn, po_id]);

  // Debug items state changes
  // useEffect(() => {
  //   console.log("📋 Items state changed:");
  //   items.forEach((item, index) => {
  //     console.log(`  Item ${index}:`, {
  //       item_id: item.item_id,
  //       item_name: item.item_name,
  //       received_quantity: item.received_quantity,
  //     });
  //   });
  // }, [items]);

  const loadWarehouses = async () => {
    console.log("🏭 Starting loadWarehouses function...");
    setWarehouseLoading(true);
    try {
      console.log("📡 Calling grnService.getWarehouses()...");
      const response = await grnService.getWarehouses();
      console.log("✅ Warehouses API response received:", response); // Debug log

      if (response && response.success && response.data) {
        // The actual warehouse array is nested in response.data.data
        const warehouseData = response.data.data || [];
        const warehouses = Array.isArray(warehouseData) ? warehouseData : [];
        console.log("🎯 Raw warehouse data:", warehouseData);
        console.log("🎯 Setting warehouse options:", warehouses); // Debug log
        console.log("📊 Number of warehouses:", warehouses.length);
        setWarehouseOptions(warehouses);
      } else {
        console.error("❌ Invalid warehouses response:", response);
        setWarehouseOptions([]);
      }
    } catch (error) {
      console.error("💥 Error loading warehouses:", error);
      if (error instanceof Error) {
        console.error("💥 Error details:", error.message, error.stack);
      }
      toast.error("Failed to load warehouses");
      setWarehouseOptions([]); // Ensure it's always an array
    } finally {
      console.log("🔄 Setting warehouse loading to false");
      setWarehouseLoading(false);
    }
  };

  const loadReceivedPOs = async () => {
    setPOLoading(true);
    try {
      const response = await grnService.getReceivedPOs({
        pageIndex: 0,
        pageSize: 50,
      });
      if (response.success && response.data) {
        const options = response.data.purchase_orders.map((po) => ({
          _id: po._id,
          po_number: po.po_number,
          vendor_name: po.vendor_details.supplier_name,
          total_amount: po.total_amount,
          currency: po.currency,
          received_at: po.received_at,
        }));
        setPOOptions(options);
      }
    } catch (error) {
      console.error("Error loading received POs:", error);
      toast.error("Failed to load received purchase orders");
    } finally {
      setPOLoading(false);
    }
  };

  const loadPOData = async (poId: string) => {
    setAutoFillLoading(true);
    try {
      const response = await grnService.getGRNAutoFillData(poId);
      if (response.success && response.data) {
        autoFillFromPO(response.data);
      }
    } catch (error) {
      console.error("Error loading PO data:", error);
      toast.error("Failed to load purchase order data");
    } finally {
      setAutoFillLoading(false);
    }
  };

  const populateFormFromGRN = () => {
    if (!grn) return;

    setSelectedPO({
      _id: grn.po_reference.po_id,
      po_number: grn.po_reference.po_number,
      vendor_name: grn.po_reference.vendor_details.supplier_name,
      total_amount: grn.po_reference.total_amount,
      currency: grn.po_reference.currency,
      received_at: grn.received_date,
    });

    setSelectedWarehouse({
      id: grn.warehouse_id,
      name: grn.warehouse_name,
      code: grn.warehouse_id,
      is_active: true, // Assume active when loading from existing GRN
    });

    setStorageLocation(grn.storage_location || "");
    setDeliveryReceiptNumber(grn.delivery_receipt_number || "");
    setReceiverNotes(grn.receiver_notes || "");
    setItems(grn.items);
  };

  const autoFillFromPO = (data: GRNAutoFillData) => {
    const { po_details, items: poItems } = data;

    console.log("🔄 Auto-filling from PO data:", data);
    console.log("📦 PO Details:", po_details);
    console.log("📋 PO Items:", poItems);

    setSelectedPO({
      _id: po_details.po_id,
      po_number: po_details.po_number,
      vendor_name: po_details.vendor_details.supplier_name,
      total_amount: 0,
      currency: "USD",
      received_at: Date.now(),
    });

    const grnItems: GRNItem[] = poItems.map((item) => {
      console.log("🏷️ Processing PO item:", JSON.stringify(item, null, 2));
      const grnItem = {
        item_id: item.item_id,
        item_name: item.item_name,
        item_sku: item.item_sku,
        ordered_quantity: item.ordered_quantity,
        received_quantity: 0,
        unit: item.unit,
        unit_price: item.unit_price,
        total_cost: 0,
        condition: "good" as ItemCondition,
        is_batch_tracked: item.is_batch_tracked ?? true, // Default to true for batch tracking
        is_expiry_tracked: item.is_expiry_tracked ?? true, // Default to true for expiry tracking
        batch_number:
          item.is_batch_tracked ?? true
            ? grnService.generateBatchNumber()
            : undefined,
        expiry_date:
          item.is_expiry_tracked ?? true
            ? dayjs().add(30, "days").valueOf()
            : undefined,
      };
      console.log("➡️ Generated GRN item:", JSON.stringify(grnItem, null, 2));
      return grnItem;
    });

    console.log("✅ Generated GRN Items:", grnItems);
    setItems(grnItems);
  };

  const handlePOSelect = async (po: POOption | null) => {
    setSelectedPO(po);
    if (po) {
      await loadPOData(po._id);
    } else {
      setItems([]);
    }
  };

  const handleReceiveAll = () => {
    console.log("🎯 handleReceiveAll called");
    console.log("🎯 Items before receive all:", JSON.stringify(items, null, 2));

    const updatedItems = items.map((item) => ({
      ...item,
      received_quantity: item.ordered_quantity,
      total_cost: item.ordered_quantity * item.unit_price,
    }));

    console.log(
      "✅ Items after receive all:",
      JSON.stringify(updatedItems, null, 2)
    );
    setItems(updatedItems);
  };

  const handleItemChange = (
    index: number,
    field: keyof GRNItem,
    value: any
  ) => {
    // Debug: Uncomment for detailed item change tracking
    console.log(
      `🔄 handleItemChange - Index: ${index}, Field: ${field}, Value:`,
      value
    );

    if (index >= items.length) {
      console.error(
        `❌ ERROR: Index ${index} is out of bounds for items array of length ${items.length}`
      );
      return;
    }

    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };

    // Recalculate total cost when quantity changes
    if (field === "received_quantity") {
      updatedItems[index].total_cost =
        (value || 0) * updatedItems[index].unit_price;
    }

    setItems(updatedItems);
  };

  const generateBatchForItem = (index: number) => {
    console.log(`🏷️ generateBatchForItem called for index ${index}`);
    console.log("🏷️ Current item:", JSON.stringify(items[index], null, 2));
    handleItemChange(index, "batch_number", grnService.generateBatchNumber());
  };

  // Ingredient Configuration Handlers (Phase 2)
  const handleOpenIngredientConfig = (item: GRNItem, index: number) => {
    setSelectedItemForConfig({ ...item, _itemIndex: index } as any);
    setIngredientConfigOpen(true);
  };

  const handleSaveIngredientConfig = (updatedItem: GRNItem) => {
    const itemIndex = (updatedItem as any)._itemIndex;
    if (typeof itemIndex === "number") {
      const updatedItems = [...items];
      updatedItems[itemIndex] = updatedItem;
      setItems(updatedItems);
    }
    setIngredientConfigOpen(false);
    setSelectedItemForConfig(null);
  };

  const handleCloseIngredientConfig = () => {
    setIngredientConfigOpen(false);
    setSelectedItemForConfig(null);
  };

  const handleSubmit = async (
    status: "draft" | "pending_approval" = "draft"
  ) => {
    // Validate form
    const validation = validateForm();
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setSaving(true);
    setErrors([]);

    try {
      console.log(
        "🔍 DEBUG: Items state before prepareFormData:",
        JSON.stringify(items, null, 2)
      );
      const formData = prepareFormData();

      if (grn) {
        // Update existing GRN
        const updateData: UpdateGRNRequest = {
          _id: grn._id,
          ...formData,
        };

        const response = await grnService.updateGRN(updateData);
        if (response.success && response.data) {
          toast.success("GRN updated successfully");
          onSave(response.data);
        }
      } else {
        // Create new GRN
        const createData: CreateGRNRequest = {
          po_id: selectedPO!._id,
          ...formData,
        };

        const response = await grnService.createGRN(createData);
        if (response.success && response.data) {
          toast.success("GRN created successfully");
          onSave(response.data.grn);
        }
      }
    } catch (error: any) {
      console.error("Error saving GRN:", error);
      toast.error(error.message || "Failed to save GRN");
    } finally {
      setSaving(false);
    }
  };

  const prepareFormData = () => {
    console.log("📝 Preparing form data...");
    console.log("📋 All items before filtering:", items);
    console.log("🔍 Selected PO:", selectedPO);
    console.log("🏭 Selected Warehouse:", selectedWarehouse);

    const filteredItems = items.filter((item) => item.received_quantity > 0);
    console.log("🔍 Filtered items (received_quantity > 0):", filteredItems);

    const formData = {
      warehouse_id: selectedWarehouse!.id,
      storage_location: storageLocation.trim() || undefined,
      delivery_receipt_number: deliveryReceiptNumber.trim() || undefined,
      receiver_notes: receiverNotes.trim() || undefined,
      items: filteredItems.map((item) => {
        const itemData = {
          item_id: item.item_id,
          received_quantity: item.received_quantity,
          condition: item.condition,
          batch_number: item.batch_number || undefined,
          expiry_date: item.expiry_date || undefined,
          remarks: item.remarks || undefined,
          // Phase 2: Tracking flags
          is_batch_tracked: item.is_batch_tracked || false,
          is_expiry_tracked: item.is_expiry_tracked || false,
          // Phase 2: Ingredient configuration
          create_as_ingredient: item.create_as_ingredient || false,
          ingredient_exists: item.ingredient_exists || false,
          existing_ingredient_id: item.existing_ingredient_id || undefined,
          // Phase 2: Ingredient details (only if creating as ingredient)
          ...(item.create_as_ingredient && {
            description: item.description || undefined,
            category: item.category || undefined,
            base_unit: item.base_unit || undefined,
            conversion_factor: item.conversion_factor || 1,
            min_stock_level: item.min_stock_level || 0,
            max_stock_level: item.max_stock_level || 0,
            storage_instructions: item.storage_instructions || undefined,
            allergens: item.allergens || undefined,
            brand_name: item.brand_name || undefined,
            inspection_notes: item.inspection_notes || undefined,
          }),
        };
        console.log(
          "🏷️ Preparing item data (with Phase 2 fields):",
          JSON.stringify(itemData, null, 2)
        );
        return itemData;
      }),
    };

    console.log(
      "📤 Final form data to be sent:",
      JSON.stringify(formData, null, 2)
    );
    return formData;
  };

  const validateForm = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!selectedPO) {
      errors.push("Purchase Order selection is required");
    }

    if (!selectedWarehouse) {
      errors.push("Warehouse selection is required");
    }

    const receivedItems = items.filter((item) => item.received_quantity > 0);
    if (receivedItems.length === 0) {
      errors.push("At least one item must be received");
    }

    // Validate items
    receivedItems.forEach((item, index) => {
      if (item.received_quantity > item.ordered_quantity) {
        errors.push(
          `${item.item_name}: Received quantity cannot exceed ordered quantity`
        );
      }

      if (item.is_batch_tracked && !item.batch_number?.trim()) {
        errors.push(
          `${item.item_name}: Batch number is required for batch-tracked items`
        );
      }

      if (item.is_expiry_tracked && !item.expiry_date) {
        errors.push(
          `${item.item_name}: Expiry date is required for expiry-tracked items`
        );
      }

      if (
        (item.condition === "damaged" ||
          item.condition === "partially_damaged") &&
        !item.remarks?.trim()
      ) {
        errors.push(
          `${item.item_name}: Remarks are required for damaged items`
        );
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const calculateTotalValue = () => {
    return items.reduce((total, item) => total + (item.total_cost || 0), 0);
  };

  const formatCurrency = (amount: number, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  if (autoFillLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading purchase order data...</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ maxWidth: 1200, margin: "0 auto", p: 3 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Icon>inventory</Icon>
            {grn ? `Edit GRN - ${grn.grn_number}` : "Create Goods Receipt Note"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {grn
              ? "Modify the goods receipt note details below"
              : "Create a new goods receipt note to receive items from a purchase order"}
          </Typography>
        </Paper>

        {/* Error Display */}
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Please fix the following errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* PO Selection */}
        {!grn && !po_id && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Purchase Order Selection
            </Typography>
            <Autocomplete
              options={poOptions}
              getOptionLabel={(option) =>
                `${option.po_number} - ${option.vendor_name}`
              }
              value={selectedPO}
              onChange={(_, value) => handlePOSelect(value)}
              loading={poLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Purchase Order"
                  required
                  placeholder="Search by PO number or vendor name..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {poLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">{option.po_number}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.vendor_name} •{" "}
                      {formatCurrency(option.total_amount, option.currency)}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Paper>
        )}

        {/* GRN Details */}
        {selectedPO && (
          <>
            {/* PO Information (Read-only) */}
            <Paper
              sx={{
                p: 3,
                mb: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <Typography variant="h6" gutterBottom>
                Purchase Order Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    PO Number
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedPO.po_number}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Vendor
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedPO.vendor_name}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Total Amount
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatCurrency(
                      selectedPO.total_amount,
                      selectedPO.currency
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Received Date
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {dayjs(selectedPO.received_at).format("MMM DD, YYYY")}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* GRN Configuration */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Receipt Configuration
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={
                      Array.isArray(warehouseOptions) ? warehouseOptions : []
                    }
                    getOptionLabel={(option) =>
                      option.code &&
                      option.code !== "undefined" &&
                      option.code !== undefined &&
                      option.code !== null &&
                      option.code !== ""
                        ? `${option.name} (${option.code})`
                        : option.name
                    }
                    value={selectedWarehouse}
                    onChange={(_, value) => setSelectedWarehouse(value)}
                    loading={warehouseLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Warehouse / Storage Location"
                        required
                        placeholder="Select warehouse..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {warehouseLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Specific Storage Location (Optional)"
                    value={storageLocation}
                    onChange={(e) => setStorageLocation(e.target.value)}
                    placeholder="e.g., Aisle 3, Shelf B, Cold Storage..."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Delivery Receipt Number (Optional)"
                    value={deliveryReceiptNumber}
                    onChange={(e) => setDeliveryReceiptNumber(e.target.value)}
                    placeholder="Enter delivery receipt or invoice number..."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Receiver Notes"
                    value={receiverNotes}
                    onChange={(e) => setReceiverNotes(e.target.value)}
                    multiline
                    rows={2}
                    placeholder="Add any notes about the delivery or receipt..."
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Items Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Received Items</Typography>
                <Button
                  variant="outlined"
                  onClick={handleReceiveAll}
                  startIcon={<Icon>select_all</Icon>}
                  disabled={items.length === 0}
                >
                  Receive All
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    const updatedItems = items.map((item) => ({
                      ...item,
                      create_as_ingredient: item.received_quantity > 0,
                      category: item.category || "Other",
                    }));
                    setItems(updatedItems);
                  }}
                  disabled={
                    items.filter((item) => item.received_quantity > 0)
                      .length === 0
                  }
                  startIcon={<Icon>science</Icon>}
                >
                  Configure All as Ingredients
                </Button>
              </Box>

              {items.length > 0 ? (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Item Details</TableCell>
                          <TableCell align="center">Ordered</TableCell>
                          <TableCell align="center">Received</TableCell>
                          <TableCell align="center">Condition</TableCell>
                          <TableCell align="center">Batch/Expiry</TableCell>
                          <TableCell align="center">Ingredient</TableCell>
                          <TableCell>Remarks</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item, index) => (
                          <TableRow key={`${item.item_id}-${index}`}>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {item.item_name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  SKU: {item.item_sku} •{" "}
                                  {formatCurrency(item.unit_price)}/{item.unit}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">
                                {item.ordered_quantity} {item.unit}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={item.received_quantity}
                                onChange={(e) =>
                                  handleItemChange(
                                    index,
                                    "received_quantity",
                                    Number(e.target.value)
                                  )
                                }
                                inputProps={{
                                  min: 0,
                                  max: item.ordered_quantity,
                                  step: 0.001,
                                }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value={item.condition}
                                  onChange={(e) =>
                                    handleItemChange(
                                      index,
                                      "condition",
                                      e.target.value
                                    )
                                  }
                                >
                                  {conditionOptions.map((option) => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      <Chip
                                        label={option.label}
                                        size="small"
                                        color={option.color as any}
                                        variant="outlined"
                                      />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                  minWidth: 250,
                                }}
                              >
                                {/* Batch Tracking Toggle */}
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={item.is_batch_tracked}
                                      onChange={(e) => {
                                        handleItemChange(
                                          index,
                                          "is_batch_tracked",
                                          e.target.checked
                                        );
                                        if (
                                          e.target.checked &&
                                          !item.batch_number
                                        ) {
                                          handleItemChange(
                                            index,
                                            "batch_number",
                                            grnService.generateBatchNumber()
                                          );
                                        }
                                      }}
                                    />
                                  }
                                  label="Batch Tracking"
                                  sx={{ mb: 0.5 }}
                                />

                                {item.is_batch_tracked && (
                                  <TextField
                                    size="small"
                                    label="Batch Number"
                                    value={item.batch_number || ""}
                                    onChange={(e) =>
                                      handleItemChange(
                                        index,
                                        "batch_number",
                                        e.target.value
                                      )
                                    }
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Tooltip title="Generate batch number">
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                generateBatchForItem(index)
                                              }
                                            >
                                              <Icon fontSize="small">
                                                refresh
                                              </Icon>
                                            </IconButton>
                                          </Tooltip>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}

                                {/* Expiry Tracking Toggle */}
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={item.is_expiry_tracked}
                                      onChange={(e) => {
                                        handleItemChange(
                                          index,
                                          "is_expiry_tracked",
                                          e.target.checked
                                        );
                                        if (
                                          e.target.checked &&
                                          !item.expiry_date
                                        ) {
                                          handleItemChange(
                                            index,
                                            "expiry_date",
                                            dayjs().add(30, "days").valueOf()
                                          );
                                        }
                                      }}
                                    />
                                  }
                                  label="Expiry Tracking"
                                  sx={{ mb: 0.5 }}
                                />

                                {item.is_expiry_tracked && (
                                  <DatePicker
                                    label="Expiry Date"
                                    value={
                                      item.expiry_date
                                        ? dayjs(item.expiry_date)
                                        : null
                                    }
                                    onChange={(value) =>
                                      handleItemChange(
                                        index,
                                        "expiry_date",
                                        value?.valueOf()
                                      )
                                    }
                                    minDate={dayjs()}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        fullWidth
                                      />
                                    )}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Button
                                  size="small"
                                  variant={
                                    item.create_as_ingredient
                                      ? "contained"
                                      : "outlined"
                                  }
                                  color={
                                    item.create_as_ingredient
                                      ? "success"
                                      : "primary"
                                  }
                                  onClick={() =>
                                    handleOpenIngredientConfig(item, index)
                                  }
                                  startIcon={
                                    <Icon>
                                      {item.create_as_ingredient
                                        ? "check_circle"
                                        : "science"}
                                    </Icon>
                                  }
                                  sx={{ minWidth: 120 }}
                                >
                                  {item.create_as_ingredient
                                    ? "Configured"
                                    : "Configure"}
                                </Button>
                                {item.create_as_ingredient && (
                                  <Chip
                                    size="small"
                                    label={
                                      item.ingredient_exists
                                        ? "Update Existing"
                                        : "Create New"
                                    }
                                    color={
                                      item.ingredient_exists
                                        ? "warning"
                                        : "success"
                                    }
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                multiline
                                rows={2}
                                value={item.remarks || ""}
                                onChange={(e) =>
                                  handleItemChange(
                                    index,
                                    "remarks",
                                    e.target.value
                                  )
                                }
                                placeholder={
                                  item.condition !== "good"
                                    ? "Required for damaged items..."
                                    : "Optional remarks..."
                                }
                                sx={{ minWidth: 150 }}
                                required={item.condition !== "good"}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Summary */}
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 1,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" color="textSecondary">
                          Items to Receive
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {
                            items.filter((item) => item.received_quantity > 0)
                              .length
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" color="textSecondary">
                          Total Value
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {formatCurrency(calculateTotalValue())}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" color="textSecondary">
                          Good Condition
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {
                            items.filter(
                              (item) =>
                                item.received_quantity > 0 &&
                                item.condition === "good"
                            ).length
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" color="textSecondary">
                          Needs Attention
                        </Typography>
                        <Typography variant="h6" color="warning.main">
                          {
                            items.filter(
                              (item) =>
                                item.received_quantity > 0 &&
                                item.condition !== "good"
                            ).length
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" color="textSecondary">
                          Create Ingredients
                        </Typography>
                        <Typography variant="h6" color="primary.main">
                          {
                            items.filter(
                              (item) =>
                                item.received_quantity > 0 &&
                                item.create_as_ingredient
                            ).length
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" color="textSecondary">
                          New Ingredients
                        </Typography>
                        <Typography variant="h6" color="info.main">
                          {
                            items.filter(
                              (item) =>
                                item.received_quantity > 0 &&
                                item.create_as_ingredient &&
                                !item.ingredient_exists
                            ).length
                          }
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              ) : (
                <Alert severity="info">
                  Please select a purchase order to load items for receipt.
                </Alert>
              )}
            </Paper>

            {/* Actions */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button variant="outlined" onClick={onCancel}>
                  Cancel
                </Button>
                <LoadingButton
                  variant="contained"
                  onClick={() => handleSubmit("draft")}
                  loading={saving}
                  disabled={
                    items.filter((item) => item.received_quantity > 0)
                      .length === 0
                  }
                >
                  {grn ? "Update GRN" : "Save as Draft"}
                </LoadingButton>
                {!grn && (
                  <LoadingButton
                    variant="contained"
                    color="success"
                    onClick={() => handleSubmit("pending_approval")}
                    loading={saving}
                    disabled={
                      items.filter((item) => item.received_quantity > 0)
                        .length === 0
                    }
                  >
                    Submit for Approval
                  </LoadingButton>
                )}
              </Box>
            </Paper>
          </>
        )}
      </Box>

      {/* Ingredient Configuration Dialog */}
      <IngredientConfigDialog
        open={ingredientConfigOpen}
        onClose={handleCloseIngredientConfig}
        item={selectedItemForConfig}
        onSave={handleSaveIngredientConfig}
      />
    </LocalizationProvider>
  );
};
